/* Preserve existing Meta identifiers as cart metadata. This does not send Meta events. */
(() => {
  'use strict';

  if (window.vexMetaCartAttributionLoaded || window.Shopify?.designMode || !window.fetch || !window.AbortController) return;
  window.vexMetaCartAttributionLoaded = true;

  const keys = ['_fbc', '_fbp'];
  const root = window.Shopify?.routes?.root || '/';
  const cartRoot = root.endsWith('/') ? root : `${root}/`;
  const maxFailures = 3;
  let missingCookieGraceUntil = Date.now() + 5000;
  let revision = 0;
  let lastSignature = null;
  let failures = 0;
  let running = false;
  let pending = false;
  let timer = null;
  let privacyLoading = false;
  let verifyAfterWriteFailure = false;

  // Use Shopify's regional consent decisions; never grant consent ourselves.
  function permission() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.marketingAllowed || !privacy?.saleOfDataAllowed) return null;
    try {
      const marketing = privacy.marketingAllowed();
      const sharing = privacy.saleOfDataAllowed();
      if (marketing === false || sharing === false) return false;
      return marketing === true && sharing === true ? true : null;
    } catch (_) {
      return null;
    }
  }

  function desiredAttributes() {
    const allowed = permission();
    if (allowed === null) return null;
    if (!allowed) return { _fbc: '', _fbp: '' };

    const cookies = document.cookie.split(';');
    const attributes = {};
    keys.forEach((key) => {
      const cookie = cookies.find((entry) => entry.trim().startsWith(`${key}=`));
      const value = cookie ? cookie.trim().slice(key.length + 1) : '';
      // A missing cookie during pixel initialization is not yet evidence of expiry.
      if (value) attributes[key] = value;
      else if (Date.now() >= missingCookieGraceUntil) attributes[key] = '';
    });
    return attributes;
  }

  function signature(attributes) {
    return attributes === null ? null : JSON.stringify(attributes);
  }

  function schedule(delay = 250) {
    if (running) {
      pending = true;
      return;
    }
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      // No failure in this optional feature may interrupt the buying flow.
      sync().catch(() => {});
    }, delay);
  }

  function refresh() {
    loadPrivacy();
    revision += 1;
    lastSignature = null;
    failures = 0;
    schedule();
  }

  async function request(path, attributes) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4000);
    try {
      const options = {
        method: attributes ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      };
      if (attributes) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({ attributes });
        options.keepalive = true;
      }
      const response = await window.fetch(`${cartRoot}${path}`, options);
      if (!response.ok) throw new Error('Cart metadata request failed');
      return await response.json();
    } catch (error) {
      if (attributes) {
        // Aborting the client does not cancel a write already accepted by Shopify.
        // Later finite checks must read the cart even when cookies have not changed.
        verifyAfterWriteFailure = true;
        checkLater();
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function sync() {
    if (running || failures >= maxFailures) return;
    let desired = desiredAttributes();
    if (desired === null || !Object.keys(desired).length || (signature(desired) === lastSignature && !verifyAfterWriteFailure)) return;

    running = true;
    const startedAtRevision = revision;
    let completed = false;
    try {
      const cart = await request('cart.js');
      // Consent/cookies may have changed while the cart was loading.
      desired = desiredAttributes();
      if (desired === null) return;
      const changes = {};
      Object.keys(desired).forEach((key) => {
        if (String(cart.attributes?.[key] || '') !== desired[key]) changes[key] = desired[key];
      });

      if (Object.keys(changes).length) {
        // Patch only our keys: never copy back notes, discounts, items or other attributes.
        const updated = await request('cart/update.js', changes);
        const saved = Object.keys(changes).every((key) => String(updated.attributes?.[key] || '') === changes[key]);
        if (!saved) throw new Error('Cart metadata was not saved');
      }

      if (startedAtRevision === revision) lastSignature = signature(desired);
      failures = 0;
      completed = true;
    } catch (_) {
      failures += 1;
    } finally {
      running = false;
      // A consent change during an earlier POST must be reconciled after that POST.
      if (pending || (completed && signature(desiredAttributes()) !== lastSignature)) {
        pending = false;
        schedule();
      } else if (!completed && failures > 0 && failures < maxFailures) {
        schedule(1000 * failures);
      }
    }
  }

  function loadPrivacy() {
    if (permission() !== null || privacyLoading || !window.Shopify?.loadFeatures) return;
    privacyLoading = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        privacyLoading = false;
        if (!error) schedule();
      });
    } catch (_) {
      privacyLoading = false;
    }
  }

  function check() {
    loadPrivacy();
    schedule();
  }

  function checkLater() {
    // Finite checks allow asynchronously initialized pixels to create their cookies.
    [1000, 3000, 6000, 15000].forEach((delay) => window.setTimeout(check, delay));
  }

  document.addEventListener('visitorConsentCollected', () => {
    missingCookieGraceUntil = Date.now() + 5000;
    refresh();
    checkLater();
  });
  // These are existing theme success notifications; this script never emits them.
  document.addEventListener('theme:product:add', refresh);
  document.addEventListener('theme:product:added', refresh);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      loadPrivacy();
      refresh();
    }
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      loadPrivacy();
      refresh();
      checkLater();
    }
  });

  check();
  checkLater();
})();
